import { motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { Spinner } from '../../../ui/Spinner';
import { btnPrimary, btnQuiet, inputCls } from '../../../ui/styles';
import { ease } from '../../../core/format/motion';
import { photoConsent } from '../../../data/policies';

/** `preview`: een tijdelijke blob-URL om de foto te tonen; `photo`: het bestand dat geüpload wordt. */
export type Draft = {
  preview: string;
  photo: Blob;
  note: string;
};
type Props = {
  draft: Draft;
  title: string;
  saving: boolean;
  /** Eerste foto (of nieuwe toestemmingstekst): eerst toestemming om foto's te bewaren. */
  needsConsent: boolean;
  consent: boolean;
  onConsent: (on: boolean) => void;
  onChange: (draft: Draft) => void;
  onSave: () => void;
  onRetake: () => void;
};

/** Nieuwe foto: bekijken, een notitie erbij, bewaren. */
export function DiaryDraft({
  draft,
  title,
  saving,
  needsConsent,
  consent,
  onConsent,
  onChange,
  onSave,
  onRetake
}: Props) {
  const reduce = useReducedMotion();
  const blocked = needsConsent && !consent;
  return <motion.section initial={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    height: 0
  }} animate={reduce ? {
    opacity: 1
  } : {
    opacity: 1,
    height: 'auto'
  }} exit={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    height: 0
  }} transition={{
    duration: 0.24,
    ease
  }} className="mb-8 overflow-hidden" aria-labelledby="diary-draft-title" data-id="element-6268" data-mp="1eo5037r3a0u0">
      <form className="grid gap-5 bg-shell p-5 ring-1 ring-line sm:grid-cols-[200px_1fr] sm:p-6" onSubmit={e => {
      e.preventDefault();
      if (!blocked) onSave();
    }} data-id="element-6269" data-mp="vqap4o6ehf8z">
        <img src={draft.preview} alt="Je nieuwe foto, nog niet bewaard" className="aspect-[3/4] w-full object-cover sm:w-[200px]" data-id="element-6270" data-mp="14pfqe29cjmou" />
        <div className="flex flex-col" data-id="element-6271" data-mp="19n7hiu2634rz">
          <h2 id="diary-draft-title" className="font-display text-[22px] font-normal text-ink" data-id="element-6272" data-mp="2g5w0rh61nd4u">
            {title}
          </h2>
          <label htmlFor="diary-note" className="mt-4 block text-[14px] text-ink" data-id="element-6273" data-mp="2l2w43kbie1s9">
            Wat valt je op? <span className="text-muted" data-id="element-6274" data-mp="l0msxtcu7t7q">(optioneel)</span>
          </label>
          <textarea id="diary-note" maxLength={1000} value={draft.note} onChange={e => onChange({
          ...draft,
          note: e.target.value
        })} rows={3} placeholder="Bijv. minder rood rond mijn neus" className={`${inputCls} mt-1.5 resize-none`} data-id="element-6275" data-mp="h1e3h0otyhhz" />

          {needsConsent && <label className="mt-5 flex cursor-pointer items-start gap-3 bg-sand p-4 text-[14px] leading-relaxed text-ink" data-id="element-6276" data-mp="3ti16c4gygzpt">
              <input type="checkbox" checked={consent} onChange={e => onConsent(e.target.checked)} aria-describedby="diary-consent-hint" className="mt-0.5 h-5 w-5 shrink-0 accent-clay" data-id="element-6277" data-mp="c21b60f6wi80" />
              <span data-id="element-6278" data-mp="3art5jt0qalcs">
                {photoConsent.label}
                <span id="diary-consent-hint" className="mt-1 block text-[13px] text-muted" data-id="element-6279" data-mp="3ar9o0dezpyza">
                  {photoConsent.hint}
                </span>
              </span>
            </label>}

          <div className="mt-auto flex flex-wrap gap-2 pt-5" data-id="element-6280" data-mp="1v1ilxrre5m75">
            <button type="submit" disabled={saving || blocked} className={btnPrimary} data-id="element-6281" data-mp="1h9u8nl5eorol">
              {saving && <Spinner data-id="element-6282" data-mp="1lbfsjqgcy68a" />}
              {saving ? 'Bezig met bewaren…' : 'Bewaar in mijn dagboek'}
            </button>
            <button type="button" onClick={onRetake} disabled={saving} className={btnQuiet} data-id="element-6283" data-mp="290i88srflp05">
              <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6284" data-mp="2dxs1yklusmts" /> Andere foto
            </button>
          </div>
        </div>
      </form>
    </motion.section>;
}